import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  MessageStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { orderBy } from 'lodash';
import { useMemo } from 'react';

/**
 * The backend now denormalizes `scheduledSendAt` (the SCHEDULED send time)
 * alongside `sendAt` (the ACTUAL sent time) on each message. We sort/pair by the
 * scheduled time so a message ties with its co-scheduled task's `start`, while
 * still DISPLAYING `sendAt` (the real received date). `@dadohr/shared@1.9.29`
 * doesn't yet type this field (a published bump is a separate release), so we
 * widen the type locally at the access sites until the field is published.
 */
type MsgWithScheduledSendAt = IExperienceMessageDenormalized & {
  scheduledSendAt?: Timestamp;
};

export const filterTasksByStatus = (
  instance: Doc<IExperienceDenormalizedV2>,
  statuses: TaskStatus[]
) => instance.data.tasks.filter((tsk) => statuses.includes(tsk.status));

export type EmployeeAppMessageStatus = 'CURRENT' | 'LATER' | 'ARCHIVED';

export const getMessageStatus = (
  message: IExperienceMessageDenormalized
): EmployeeAppMessageStatus => {
  // Checking for explicit false, as this indicates an explicit cop-out
  if (
    message.markedAsRead?.read ||
    (message.archived && message.markedAsRead?.read !== false)
  ) {
    return 'ARCHIVED';
  }
  return message.status === MessageStatus.UPCOMING ? 'LATER' : 'CURRENT';
};

export const getMessageStatuses = (
  instance: Doc<IExperienceDenormalizedV2>
): {
  [msgId: string]: EmployeeAppMessageStatus;
} => {
  return Object.fromEntries(
    instance.data.messages.map((m) => [m.templateId, getMessageStatus(m)])
  );
};

export const useGroupedTaskAndMessages = (
  tasks: ITaskDenormalized[],
  messages: IExperienceMessageDenormalized[]
) => {
  return useMemo(() => {
    return orderBy(
      [
        ...tasks.map<{ type: 'TASK'; item: ITaskDenormalized }>((task) => ({
          type: 'TASK',
          item: task
        })),
        ...messages.map<{
          type: 'MSG';
          item: IExperienceMessageDenormalized;
        }>((msg) => ({ type: 'MSG', item: msg }))
      ],
      [
        (x) =>
          x.type === 'TASK'
            ? (x.item.start?.toMillis() || 0)
            : ((x.item as MsgWithScheduledSendAt).scheduledSendAt ??
                x.item.sendAt)?.toMillis() || 0,
        (x) => (x.type === 'TASK' ? 1 : 0)
      ]
    );
  }, [tasks, messages]);
};

export const getAccompanyingMessage = (
  msgs: IExperienceMessageDenormalized[],
  task: ITaskDenormalized | undefined | null
) => {
  return (
    (task &&
      msgs.find((m) =>
        ((m as MsgWithScheduledSendAt).scheduledSendAt ?? m.sendAt).isEqual(
          task.start
        )
      )) ||
    null
  );
};
